import { useState } from 'react'
import { Link } from 'react-router-dom'
import { api, qs } from '../api/client'
import type { AuditLog, Paged } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { Empty, Modal, Notice, Pager, fmtTime } from '../components/ui'

const ACTIONS = [
  '',
  'LOGIN',
  'LOGIN_FAIL',
  'INTAKE_COMMIT',
  'SOLD',
  'RESTORED',
  'OUTBOUND_UNDO',
  'INTAKE_CANCEL',
  'DOMAIN_EDIT',
  'RETAIL_COMMIT',
  'BACKUP',
]

export function Audit() {
  const [action, setAction] = useState('')
  const [page, setPage] = useState(1)
  const [open, setOpen] = useState<AuditLog | null>(null)
  const { data, error, loading, reload } = useAsync(
    () => api.get<Paged<AuditLog>>(`/api/audit-logs${qs({ action, page, size: 50 })}`),
    [action, page],
  )
  const rows = data?.items ?? []

  return (
    <section>
      <header className="page-head compact">
        <h1>审计日志</h1>
        <div className="row">
          <select
            aria-label="按动作筛选"
            value={action}
            onChange={(e) => {
              setAction(e.target.value)
              setPage(1)
            }}
          >
            {ACTIONS.map((a) => (
              <option key={a} value={a}>
                {a || '全部动作'}
              </option>
            ))}
          </select>
          <button type="button" onClick={reload} disabled={loading}>
            {loading ? '载入中…' : '刷新'}
          </button>
        </div>
      </header>

      <Notice error={error} />
      {!loading && rows.length === 0 && <Empty text="没有匹配的审计记录。" />}

      {rows.length > 0 && (
        <div className="card table-card">
          <table className="table">
            <thead>
              <tr>
                <th>时间</th>
                <th>动作</th>
                <th>操作人</th>
                <th>对象</th>
                <th>说明</th>
                <th>请求编号</th>
                <th>IP</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id}>
                  <td className="muted">{fmtTime(a.actedAt)}</td>
                  <td>
                    <code>{a.action}</code>
                  </td>
                  <td>{a.actorName || '-'}</td>
                  <td>
                    {a.entity} #{a.entityId || '-'}
                    {a.batchId ? (
                      <>
                        {' · '}
                        <Link to={`/batches?highlight=${a.batchId}`}>批次 #{a.batchId}</Link>
                      </>
                    ) : null}
                  </td>
                  <td className="muted">{a.detail || '-'}</td>
                  <td className="mono small">{a.requestId || '-'}</td>
                  <td className="mono small">{a.ip || '-'}</td>
                  <td>
                    <button type="button" className="link" onClick={() => setOpen(a)}>
                      详情
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={page} size={50} total={data?.total ?? 0} onPage={setPage} />
        </div>
      )}

      {open && (
        <Modal title={`${open.action} · #${open.id}`} onClose={() => setOpen(null)} width={720}>
          <p className="muted">
            {fmtTime(open.actedAt)} · {open.actorName || '匿名'} · 请求 {open.requestId || '-'} · IP {open.ip || '-'}
          </p>
          {open.detail && <p>{open.detail}</p>}
          <JsonBlock label="变更前" raw={open.beforeJson} />
          <JsonBlock label="变更后" raw={open.afterJson} />
        </Modal>
      )}
    </section>
  )
}

function JsonBlock({ label, raw }: { label: string; raw?: string }) {
  if (!raw) return null
  let pretty = raw
  try {
    pretty = JSON.stringify(JSON.parse(raw), null, 2)
  } catch {
    /* keep as-is */
  }
  return (
    <div className="json-block">
      <h3>{label}</h3>
      <pre>{pretty}</pre>
    </div>
  )
}
